import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import buildInfo from '../buildId.json';

// Polls the server's fresh /version.json and compares its buildId against the
// id baked into THIS bundle at compile time. A mismatch means a newer build is
// live, so the running (stale) tab should reload to pick it up.
//
// - Production only: dev builds never prompt (NODE_ENV !== 'production').
// - Web only: disabled on native (Capacitor) builds — a store-distributed
//   native binary's baked id rarely equals the web server's latest, so it would
//   perpetually prompt and reloading the WebView wouldn't update the native app
//   anyway (native updates ship via the app store, not window.location.reload).
// - Never prompts on first load: only a real id mismatch flips updateAvailable.
// - Once a mismatch is detected, checking stops (no point re-checking).
// - Fetch/parse errors are swallowed silently (offline, transient 5xx, etc.).

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const INITIAL_DELAY_MS = 5000; // don't race app startup

export default function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // Ref mirror so event/interval handlers can short-circuit without re-binding.
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    // Web only: never poll/prompt inside a native Capacitor WebView.
    if (Capacitor.isNativePlatform()) return;

    let initialTimer;
    let intervalTimer;

    const check = async () => {
      if (stoppedRef.current) return;
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const serverId = data && data.buildId;
        if (
          serverId &&
          serverId !== 'unknown' &&
          serverId !== buildInfo.buildId
        ) {
          stoppedRef.current = true;
          setUpdateAvailable(true);
          if (intervalTimer) clearInterval(intervalTimer);
        }
      } catch (err) {
        // Swallow: offline / transient failure. Will retry on next tick/event.
      }
    };

    initialTimer = setTimeout(check, INITIAL_DELAY_MS);
    intervalTimer = setInterval(check, POLL_INTERVAL_MS);

    const onFocus = () => check();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalTimer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return { updateAvailable };
}
